/**
 * Gère les erreurs API et retourne un message d'erreur formaté
 * @param {Error} error - L'erreur à gérer
 * @param {string} defaultMessage - Message par défaut si aucune information n'est disponible
 * @returns {string} - Message d'erreur formaté
 */
export const handleApiError = (error, defaultMessage = 'Une erreur est survenue') => {
    // Si l'erreur n'a pas de réponse, c'est probablement une erreur réseau
    if (!error.response) {
        return error.message || defaultMessage;
    }

    const status = error.response.status;
    const responseData = error.response.data;

    // Gestion spécifique selon le code de statut
    switch (status) {
        case 400:
            // Bad Request - erreur de validation ou requête mal formée
            if (responseData?.detail) {
                // Si detail est un tableau (format FastAPI/Pydantic)
                if (Array.isArray(responseData.detail)) {
                    const errorMessages = responseData.detail
                        .map((error) => error.msg || error.message || JSON.stringify(error))
                        .filter(Boolean)
                        .join('; ');
                    return errorMessages || 'Erreur de validation';
                }
                // Si detail est une chaîne
                if (typeof responseData.detail === 'string') {
                    return `Erreur de validation: ${responseData.detail}`;
                }
                // Si detail est un objet avec un message
                if (responseData.detail.msg || responseData.detail.message) {
                    return responseData.detail.msg || responseData.detail.message;
                }
                return `Erreur de validation: ${JSON.stringify(responseData.detail)}`;
            }
            if (responseData?.message) {
                return `Erreur: ${responseData.message}`;
            }
            if (responseData?.errors) {
                // Erreurs de validation détaillées
                const errorMessages = Object.entries(responseData.errors)
                    .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
                    .join('; ');
                return `Erreurs de validation: ${errorMessages}`;
            }
            return 'Requête invalide. Veuillez vérifier les données saisies.';

        case 401:
            // Unauthorized
            return responseData?.detail || 'Vous n\'êtes pas autorisé à effectuer cette action.';

        case 403:
            // Forbidden
            return responseData?.detail || 'Accès interdit.';

        case 404:
            // Not Found
            if (responseData?.detail) {
                return `Ressource non trouvée: ${responseData.detail}`;
            }
            return 'La ressource demandée n\'existe pas.';

        case 409:
            // Conflict
            return responseData?.detail || 'Un conflit est survenu. La ressource existe déjà ou a été modifiée.';

        case 422:
            // Unprocessable Entity
            if (responseData?.detail) {
                // Si detail est un tableau (format FastAPI/Pydantic)
                if (Array.isArray(responseData.detail)) {
                    const errorMessages = responseData.detail
                        .map((error) => error.msg || error.message || JSON.stringify(error))
                        .filter(Boolean)
                        .join('; ');
                    return errorMessages || 'Erreur de validation';
                }
                // Si detail est une chaîne
                if (typeof responseData.detail === 'string') {
                    return `Erreur de validation: ${responseData.detail}`;
                }
                // Si detail est un objet avec un message
                if (responseData.detail.msg || responseData.detail.message) {
                    return responseData.detail.msg || responseData.detail.message;
                }
                return `Erreur de validation: ${JSON.stringify(responseData.detail)}`;
            }
            if (responseData?.errors) {
                const errorMessages = Object.entries(responseData.errors)
                    .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
                    .join('; ');
                return `Erreurs de validation: ${errorMessages}`;
            }
            return 'Les données fournies ne sont pas valides.';

        case 500:
            // Internal Server Error
            return 'Erreur serveur. Veuillez réessayer plus tard.';

        case 502:
        case 503:
        case 504:
            // Bad Gateway, Service Unavailable, Gateway Timeout
            return 'Service temporairement indisponible. Veuillez réessayer plus tard.';

        default:
            // Autres erreurs
            if (responseData?.detail) {
                return responseData.detail;
            }
            if (responseData?.message) {
                return responseData.message;
            }
            return defaultMessage;
    }
};

/**
 * Extrait le code de statut HTTP de l'erreur
 * @param {Error} error - L'erreur
 * @returns {number|null} - Le code de statut ou null
 */
export const getErrorStatus = (error) => {
    return error.response?.status || null;
};

/**
 * Vérifie si l'erreur est une erreur 404
 * @param {Error} error - L'erreur
 * @returns {boolean} - True si c'est une erreur 404
 */
export const isNotFoundError = (error) => {
    return getErrorStatus(error) === 404;
};

/**
 * Vérifie si l'erreur est une erreur 400
 * @param {Error} error - L'erreur
 * @returns {boolean} - True si c'est une erreur 400
 */
export const isBadRequestError = (error) => {
    return getErrorStatus(error) === 400;
};

/**
 * Message d'erreur adapté aux appels longs (timeout, réseau, HTTP).
 */
export const formatRequestError = (error, defaultMessage, labels = {}) => {
    if (!error) return defaultMessage;

    if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
        return labels.timeout
            || 'Délai d\'attente dépassé. Le traitement peut prendre plusieurs minutes — réessayez.';
    }

    if (!error.response) {
        if (error.message === 'Network Error') {
            return labels.network || 'Erreur réseau. Vérifiez votre connexion et réessayez.';
        }
        return error.message || defaultMessage;
    }

    return handleApiError(error, defaultMessage);
};


